/**
 * The two things every microphone in this app does with the allowance — one copy,
 * shared by the composer's mic and the note sheet's (M4: one idea, one copy).
 *
 *  - BEFORE recording: if today's allowance is known to be gone, the tap opens the
 *    sheet instead of the mic. The service would refuse the clip anyway (fail closed
 *    is ITS job); refusing here saves the person a recording that goes nowhere.
 *  - AFTER a refusal: a 429 `quota` carries today's numbers — keep them, so the next
 *    tap is gated without another round trip — and open the same sheet. Every other
 *    failure stays one sentence on the status line, the service's own words.
 *  - The cap on a recording is min(the service's 120 s, what is left today) —
 *    avg-voice's rule ①: a clip that would cross the limit is refused WHOLE, so the
 *    recorder must stop before it would.
 */
import { VoiceError } from '../voice/transcribe'
import { MAX_RECORD_MS } from '../voice/useVoiceRecorder'
import { billingUiOn } from './flags'
import { getBilling, isExhausted, noteQuotaRefusal } from './plan'
import { showQuotaPrompt } from './quotaPrompt'

/** True when the tap was taken by the gate; false = go ahead and record. */
export function micGate(state: string): boolean {
  if (!billingUiOn()) return false // no paywall in this build: the service alone decides
  if (state !== 'idle') return false
  if (!isExhausted()) return false
  showQuotaPrompt()
  return true
}

/** How long the NEXT recording may run, in ms. Unknown usage = the service's cap. */
export function recordCapMs(): number {
  const u = getBilling().usage
  if (!u) return MAX_RECORD_MS
  return Math.max(1_000, Math.min(MAX_RECORD_MS, Math.floor(u.remainingS * 1000)))
}

/** Route a transcribe failure: quota → the sheet; anything else → the status line. */
export function reportVoiceError(e: unknown, status: (message: string) => void): void {
  if (billingUiOn() && e instanceof VoiceError && e.code === 'quota') {
    noteQuotaRefusal(e.details)
    showQuotaPrompt()
    return
  }
  status(e instanceof VoiceError ? e.message : 'Transcription failed. Try again.')
}
